import BigNumber from 'bignumber.js';
import type { Address } from 'viem';

import {
  legacyPoolComptrollerAbi,
  primeAbi,
  resilientOracleAbi,
  vaiControllerAbi,
} from 'libs/contracts';
import type { Asset, TokenBalance } from 'types';
import { isDevChainId } from 'utilities';
import type { GetPoolsInput, GetPoolsOutput, PrimeApy, VTokenBalance } from '../types';
import { appendPrimeSimulationDistributions } from './appendPrimeSimulationDistributions';
import { formatOutput } from './formatOutput';
import { getApiPools } from './getApiPools';
import { getLocalPools } from './getLocalPools';
import { getUserCollateralAddresses } from './getUserCollateralAddresses';
import { getUserPrimeApys } from './getUserPrimeApys';
import { getUserTokenBalances } from './getUserTokenBalances';
import { getUserVaiBorrowBalance } from './getUserVaiBorrowBalance';
import { type MulticallResult, unwrapMulticallResult } from './unwrapMulticallResult';

type PoolRuntimeCallKey =
  | 'primeVTokenAddresses'
  | 'primeMinimumXvsToStakeMantissa'
  | 'userPrimeToken'
  | 'vaiPriceMantissa'
  | 'vaiRepayRateMantissa';

type PoolRuntimeContract = {
  abi: typeof primeAbi | typeof resilientOracleAbi | typeof vaiControllerAbi;
  address: Address;
  functionName: string;
  args?: readonly [Address];
};

type UserPrimeToken = readonly [boolean, boolean];

export const getPools = async ({
  publicClient,
  chainId,
  accountAddress,
  primeContractAddress,
  poolLensContractAddress,
  legacyPoolComptrollerContractAddress,
  vaiControllerContractAddress,
  resilientOracleContractAddress,
  venusLensContractAddress,
  tokens,
  isEModeFeatureEnabled,
}: GetPoolsInput) => {
  const vai = tokens.find((token) => token.symbol === 'VAI');
  const poolRuntimeCallKeys: PoolRuntimeCallKey[] = [];
  const poolRuntimeContracts: PoolRuntimeContract[] = [];

  const addPoolRuntimeContract = (key: PoolRuntimeCallKey, contract: PoolRuntimeContract) => {
    poolRuntimeCallKeys.push(key);
    poolRuntimeContracts.push(contract);
  };

  if (primeContractAddress) {
    addPoolRuntimeContract('primeVTokenAddresses', {
      abi: primeAbi,
      address: primeContractAddress,
      functionName: 'getAllMarkets',
    });

    addPoolRuntimeContract('primeMinimumXvsToStakeMantissa', {
      abi: primeAbi,
      address: primeContractAddress,
      functionName: 'MINIMUM_STAKED_XVS',
    });

    if (accountAddress) {
      addPoolRuntimeContract('userPrimeToken', {
        abi: primeAbi,
        address: primeContractAddress,
        functionName: 'tokens',
        args: [accountAddress],
      });
    }
  }

  if (resilientOracleContractAddress && vai) {
    addPoolRuntimeContract('vaiPriceMantissa', {
      address: resilientOracleContractAddress,
      abi: resilientOracleAbi,
      functionName: 'getPrice',
      args: [vai.address],
    });
  }

  if (vaiControllerContractAddress) {
    addPoolRuntimeContract('vaiRepayRateMantissa', {
      address: vaiControllerContractAddress,
      abi: vaiControllerAbi,
      functionName: 'getVAIRepayRate',
    });
  }

  const [{ pools: apiPools, tokenPricesMapping }, currentBlockNumber, poolRuntimeResults] =
    await Promise.all([
      isDevChainId(chainId)
        ? getLocalPools({
            chainId,
            publicClient,
            legacyPoolComptrollerContractAddress,
            resilientOracleContractAddress,
          })
        : getApiPools({ chainId }),
      publicClient.getBlockNumber(),
      poolRuntimeContracts.length > 0
        ? publicClient.multicall({
            contracts: poolRuntimeContracts as never,
          })
        : Promise.resolve([]),
    ]);

  const getPoolRuntimeResult = <T>(key: PoolRuntimeCallKey) => {
    const resultIndex = poolRuntimeCallKeys.indexOf(key);

    if (resultIndex === -1) {
      return undefined;
    }

    const result = poolRuntimeResults[resultIndex] as unknown as MulticallResult<T>;

    return unwrapMulticallResult(result);
  };

  const unsafePrimeVTokenAddresses =
    getPoolRuntimeResult<readonly Address[]>('primeVTokenAddresses');
  const primeMinimumXvsToStakeMantissa = getPoolRuntimeResult<bigint>(
    'primeMinimumXvsToStakeMantissa',
  );
  const userPrimeToken = getPoolRuntimeResult<UserPrimeToken>('userPrimeToken');
  const vaiPriceMantissa = getPoolRuntimeResult<bigint>('vaiPriceMantissa');
  const vaiRepayRateMantissa = getPoolRuntimeResult<bigint>('vaiRepayRateMantissa');

  const primeVTokenAddresses = unsafePrimeVTokenAddresses ?? [];
  const isUserPrime = userPrimeToken?.[0] || false;

  let userCollateralVTokenAddresses: string[] | undefined;
  let userTokenBalances: TokenBalance[] | undefined;
  let userVTokenBalances: VTokenBalance[] | undefined;
  let userVaiBorrowBalanceMantissa: BigNumber | undefined;
  let userPrimeApyMap: Map<string, PrimeApy> | undefined;
  let userLegacyPoolEModeGroupId: number | undefined;

  if (accountAddress) {
    const [
      userCollaterals,
      userBalances,
      userVaiBorrowBalance,
      userPrimeApys,
      userLegacyPoolEModeGroupIdResult,
    ] = await Promise.all([
      getUserCollateralAddresses({
        chainId,
        accountAddress,
        legacyPoolComptrollerContractAddress,
        apiPools,
        publicClient,
      }),
      getUserTokenBalances({
        accountAddress,
        apiPools,
        chainId,
        tokens,
        publicClient,
        poolLensContractAddress,
        venusLensContractAddress,
      }),
      vaiControllerContractAddress
        ? getUserVaiBorrowBalance({
            accountAddress,
            publicClient,
            vaiControllerContractAddress,
          })
        : undefined,
      isUserPrime && primeContractAddress
        ? getUserPrimeApys({
            accountAddress,
            publicClient,
            primeContractAddress,
            primeVTokenAddresses,
          })
        : undefined,
      isEModeFeatureEnabled && legacyPoolComptrollerContractAddress
        ? publicClient.readContract({
            abi: legacyPoolComptrollerAbi,
            address: legacyPoolComptrollerContractAddress,
            functionName: 'userPoolId',
            args: [accountAddress],
          })
        : undefined,
    ]);

    userCollateralVTokenAddresses = userCollaterals.userCollateralAddresses;
    userVTokenBalances = userBalances.userVTokenBalances;
    userTokenBalances = userBalances.userTokenBalances;
    userVaiBorrowBalanceMantissa = userVaiBorrowBalance?.userVaiBorrowBalanceMantissa;
    userPrimeApyMap = userPrimeApys?.userPrimeApyMap;
    userLegacyPoolEModeGroupId = userLegacyPoolEModeGroupIdResult
      ? Number(userLegacyPoolEModeGroupIdResult)
      : undefined;
  }

  const userPoolEModeGroupIdMapping: Record<Address, number> = {};

  // E-mode groups are currently only enabled on the legacy Core Pool
  if (userLegacyPoolEModeGroupId && legacyPoolComptrollerContractAddress) {
    userPoolEModeGroupIdMapping[legacyPoolComptrollerContractAddress.toLowerCase() as Address] =
      userLegacyPoolEModeGroupId;
  }

  const pools = formatOutput({
    chainId,
    isUserConnected: !!accountAddress,
    tokens,
    currentBlockNumber,
    apiPools,
    tokenPricesMapping,
    userPrimeApyMap,
    userCollateralVTokenAddresses,
    userVTokenBalances,
    userTokenBalances,
    userVaiBorrowBalanceMantissa,
    userPoolEModeGroupIdMapping,
    vaiRepayRateMantissa,
    vaiPriceMantissa,
  });

  // Add Prime simulations
  // TODO: get Prime simulations from API
  const xvs = tokens.find((token) => token.symbol === 'XVS');

  if (primeContractAddress && primeMinimumXvsToStakeMantissa && xvs) {
    await appendPrimeSimulationDistributions({
      assets: pools.reduce<Asset[]>((acc, pool) => acc.concat(pool.assets), []),
      primeContractAddress,
      primeVTokenAddresses,
      primeMinimumXvsToStakeMantissa: new BigNumber(primeMinimumXvsToStakeMantissa.toString()),
      xvs,
      chainId,
      publicClient,
    });
  }

  const output: GetPoolsOutput = {
    pools,
  };

  return output;
};
